import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import useVisitorTracker from './hooks/useVisitorTracker';


// Import components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import FaviconUpdater from './components/common/FaviconUpdater';
import WhatsAppButton from './components/common/WhatsAppButton';
import AITempleAssistant from './components/common/AITempleAssistant';

// Context Providers
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { SecurityProvider } from './context/SecurityContext';
import { SiteConfigProvider } from './context/SiteConfigContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminInactivityGuard from './components/auth/AdminInactivityGuard';
import {
  AdminRoute,
  DevoteeRoute,
  SubAdminRoute,
  AccountantRoute
} from './components/auth/RoleBasedRoute';
import LoadingScreen from './components/common/LoadingScreen';
import SentryErrorBoundary from './components/common/SentryErrorBoundary';
const HomePage = React.lazy(() => import('./pages/HomePage'));
const NewsUpdatesPage = React.lazy(() => import('./pages/NewsPage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const UserDashboard = React.lazy(() => import('./pages/dashboard/UserDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/dashboard/AdminDashboard'));
const AdminAnalytics = React.lazy(() => import('./pages/dashboard/AdminAnalytics'));
const AdminFAQs = React.lazy(() => import('./pages/dashboard/AdminFAQs'));
const SubAdminDashboard = React.lazy(() => import('./pages/dashboard/SubAdminDashboard'));
const AccountantDashboard = React.lazy(() => import('./pages/dashboard/AccountantDashboard'));
const DonationPage = React.lazy(() => import('./pages/donation/DonationPage'));
const EventsPage = React.lazy(() => import('./pages/events/EventsPage'));
const FestivalsPage = React.lazy(() => import('./pages/FestivalsPage'));
const PrayerRequestPage = React.lazy(() => import('./pages/prayer/PrayerRequestPage'));
const TempleHistoryPage = React.lazy(() => import('./pages/info/TempleHistoryPage'));
const GalleryPage = React.lazy(() => import('./pages/Gallery'));
const ContactPage = React.lazy(() => import('./pages/info/ContactPage'));
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));
const TempleTimingsPage = React.lazy(() => import('./pages/temple/TempleTimingsPage'));
const SpiritualToolsPage = React.lazy(() => import('./pages/spiritual/SpiritualToolsPage'));
const BhajanLibrary = React.lazy(() => import('./pages/BhajanLibrary'));
const VirtualTour = React.lazy(() => import('./pages/VirtualTour'));
const CommunityPage = React.lazy(() => import('./pages/community/CommunityPage'));
const LiveTVPage = React.lazy(() => import('./pages/LiveTVPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const VolunteerPage = React.lazy(() => import('./pages/VolunteerPage'));
const PanchangamPage = React.lazy(() => import('./pages/PanchangamPage'));
const AnnadanamPage = React.lazy(() => import('./pages/AnnadanamPage'));
const EStorePage = React.lazy(() => import('./pages/EStorePage'));
const EHundiPage = React.lazy(() => import('./pages/EHundiPage'));
const AdminSiteConfig = React.lazy(() => import('./pages/dashboard/AdminSiteConfig'));
const PrasadamDetails = React.lazy(() => import('./pages/PrasadamDetails'));
const DonorWall = React.lazy(() => import('./pages/donors/DonorWall'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/info/PrivacyPolicyPage'));
const TermsOfUsePage = React.lazy(() => import('./pages/info/TermsOfUsePage'));


// Analytics Tracker Component to access Router hooks context
const AnalyticsTracker = () => {
  useVisitorTracker();
  return null;
};


// Create a loading component
const LoadingFull = () => (
  <LoadingScreen height="80vh" label="Loading Sri Renuka Ankammathalli Temple..." />
);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Custom Chakra UI theme matching reference site exactly
const theme = extendTheme({
  colors: {
    // Exact colors from reference site
    primary: {
      50: '#fff5f5',
      100: '#ffe0e0',
      200: '#ffcccc',
      300: '#ff9999',
      400: '#ff6666',
      500: '#620404', // Dark red - main color
      600: '#4a0303',
      700: '#3d0202',
      800: '#2f0101',
      900: '#1f0101',
    },
    gold: {
      50: '#fffef0',
      100: '#fffce0',
      200: '#fff9c0',
      300: '#fff6a0',
      400: '#fff380',
      500: '#ecbd00', // Gold - main color
      600: '#d4a800',
      700: '#bc9300',
      800: '#a47e00',
      900: '#8c6900',
    }
  },
  fonts: {
    heading: '"Playfair Display", serif',
    body: '"Roboto", sans-serif',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '500',
        borderRadius: 'md',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: '"Roboto", sans-serif',
        position: 'relative',
        overflow: 'hidden',
        willChange: 'transform',
        transform: 'translateZ(0)',
        _focus: {
          boxShadow: 'none',
        },
        _focusVisible: {
          boxShadow: 'none',
        }
      },
      sizes: {
        lg: {
          h: 9, // ~36px (reduced from 48px)
          fontSize: 'sm',
          px: 5,
        },
        md: {
          h: 8, // ~32px (reduced from 40px)
          fontSize: 'xs',
          px: 4,
        },
        sm: {
          h: 7, // ~28px (reduced from 32px)
          fontSize: 'xs',
          px: 3,
        },
        xs: {
          h: 6, // ~24px
          fontSize: '10px',
          px: 2,
        },
      },
      defaultProps: {
        colorScheme: 'primary',
      },
      variants: {
        solid: (props) => ({
          bg: 'primary.500',
          color: 'white',
          zIndex: 1,
          _before: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bg: 'primary.600',
            zIndex: -1,
            transition: 'opacity 0.4s ease-in-out',
            opacity: 0,
          },
          _hover: {
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 20px rgba(98, 4, 4, 0.3)',
            _before: {
              opacity: 1,
            }
          },
          _active: {
            transform: 'none',
            boxShadow: 'none',
            outline: 'none',
            bg: 'primary.600',
          }
        }),
        ghost: (props) => ({
          _hover: {
            bg: 'rgba(236, 189, 0, 0.15)',
            transform: 'translateY(-2px)',
            color: 'primary.500',
          },
          _active: {
            transform: 'none',
            boxShadow: 'none',
            outline: 'none',
            bg: 'transparent',
          }
        }),
        outline: (props) => ({
          _hover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 15px rgba(236, 189, 0, 0.2)',
          },
          _active: {
            transform: 'none',
            boxShadow: 'none',
            outline: 'none',
            bg: 'transparent',
          }
        })
      },
    },
    Link: {
      baseStyle: {
        transition: 'all 0.3s ease-in-out',
        _hover: {
          textDecoration: 'none',
          color: 'gold.500',
          transform: 'translateY(-2px)',
        },
        _focus: {
          boxShadow: 'none',
        },
      }
    },
    Card: {
      baseStyle: (props) => ({
        container: {
          borderRadius: '20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          bg: 'white',
          borderColor: 'transparent',
          borderWidth: '2px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          _hover: {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(98, 4, 4, 0.15)',
            borderColor: 'gold.500',
          }
        },
      }),
    },
  },
  styles: {
    global: (props) => ({
      '*, *::before, &::after': {
        _focus: {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
        _focusVisible: {
          outline: 'none !important',
          boxShadow: 'none !important',
        }
      },
      body: {
        bg: '#ffffff',
        color: '#1f2937',
        fontFamily: '"Roboto", sans-serif',
        transition: 'background-color 0.2s ease-in-out',
      },
    }),
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
});

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'dummy-client-id.apps.googleusercontent.com';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Router>
        <SentryErrorBoundary>
          <AnalyticsTracker />
          <QueryClientProvider client={queryClient}>
          <ChakraProvider theme={theme}>
            <LanguageProvider>
              <AuthProvider>
                <SecurityProvider>
                  <SiteConfigProvider>
                    <FaviconUpdater />
                    <SocketProvider>
                      <NotificationProvider>
                        <AdminInactivityGuard />
                        <div className="App">
                          <Header />
                          <main style={{ minHeight: 'calc(100vh - 200px)' }}>
                            <React.Suspense fallback={<LoadingFull />}>
                              <Routes>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/signup" element={<SignupPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                                <Route
                                  path="/dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DevoteeRoute>
                                        <UserDashboard />
                                      </DevoteeRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute>
                                      <AdminRoute>
                                        <AdminDashboard />
                                      </AdminRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/analytics"
                                  element={
                                    <ProtectedRoute>
                                      <AdminRoute>
                                        <AdminAnalytics />
                                      </AdminRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/faqs"
                                  element={
                                    <ProtectedRoute>
                                      <AdminRoute>
                                        <AdminFAQs />
                                      </AdminRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/site-config"
                                  element={
                                    <ProtectedRoute>
                                      <AdminRoute>
                                        <AdminSiteConfig />
                                      </AdminRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/sub-admin"
                                  element={
                                    <ProtectedRoute>
                                      <SubAdminRoute>
                                        <SubAdminDashboard />
                                      </SubAdminRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/accountant"
                                  element={
                                    <ProtectedRoute>
                                      <AccountantRoute>
                                        <AccountantDashboard />
                                      </AccountantRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/donate" element={<DonationPage />} />
                                <Route path="/events" element={<EventsPage />} />
                                 <Route path="/festivals" element={<FestivalsPage />} />
                                 <Route path="/festival" element={<FestivalsPage />} />
                                <Route
                                  path="/prayer-request"
                                  element={
                                    <ProtectedRoute>
                                      <DevoteeRoute>
                                        <PrayerRequestPage />
                                      </DevoteeRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/temple-history" element={<TempleHistoryPage />} />
                                <Route path="/gallery" element={<GalleryPage />} />
                                <Route path="/news" element={<NewsUpdatesPage />} />
                                 <Route
                                   path="/donor-wall"
                                   element={
                                     <ProtectedRoute>
                                       <DonorWall />
                                     </ProtectedRoute>
                                   }
                                 />
                                 <Route path="/live-tv" element={<LiveTVPage />} />
                                <Route path="/contact" element={<ContactPage />} />
                                <Route
                                  path="/notifications"
                                  element={
                                    <ProtectedRoute>
                                      <DevoteeRoute>
                                        <NotificationsPage />
                                      </DevoteeRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/profile"
                                  element={
                                    <ProtectedRoute>
                                      <DevoteeRoute>
                                        <ProfilePage />
                                      </DevoteeRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                 <Route path="/temple-timings" element={<TempleTimingsPage />} />
                                 <Route path="/abhishekam" element={<TempleTimingsPage />} />
                                 <Route path="/Abhishekam" element={<TempleTimingsPage />} />
                                 <Route path="/spiritual-tools" element={<SpiritualToolsPage />} />
                                 <Route path="/community" element={<CommunityPage />} />
                                 <Route path="/search" element={<SearchPage />} />
                                 <Route path="/bhajans" element={<BhajanLibrary />} />
                                 <Route path="/virtual-darshan" element={<VirtualTour />} />
                                 <Route path="/feedback" element={<TestimonialsPage />} />
                                <Route path="/volunteer" element={<VolunteerPage />} />
                                <Route path="/panchangam" element={<PanchangamPage />} />
                                <Route path="/annadanam" element={<AnnadanamPage />} />
                                 <Route path="/prasadam-details" element={<PrasadamDetails />} />
                                <Route path="/shop" element={<EStorePage />} />
                                <Route path="/e-hundi" element={<EHundiPage />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                                <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                                <Route path="/terms" element={<TermsOfUsePage />} />
                                <Route path="/terms-and-conditions" element={<TermsOfUsePage />} />
                                <Route path="*" element={<NotFoundPage />} />
                              </Routes>
                            </React.Suspense>
                          </main>
                          <Footer />
                          <WhatsAppButton />
                          <AITempleAssistant />
                          <ScrollToTop />
                        </div>
                        <Toaster position="top-right" />
                      </NotificationProvider>
                    </SocketProvider>
                  </SiteConfigProvider>
                </SecurityProvider>
              </AuthProvider>
            </LanguageProvider>
          </ChakraProvider>
        </QueryClientProvider>
        </SentryErrorBoundary>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
